import { Check as CheckIcon } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";
import { Nullable } from "../../../../common/types/nullable";
import { isValidAmount, isValidComment } from "./modalValidators";
import {
  TalentTaxWithholdingCorrection,
  UpdateTaxWithholdingCorrection,
} from "./types";

interface Errors {
  amount: boolean;
  date: boolean;
  comment: boolean;
}

interface Props {
  onEditCancel: () => void;
  onEditSubmit: (value: UpdateTaxWithholdingCorrection) => void;
  item: TalentTaxWithholdingCorrection;
}

const AddEditModal = ({ onEditCancel, onEditSubmit, item }: Props) => {
  const [amount, setAmount] = useState<
    TalentTaxWithholdingCorrection["amount"]
  >(item.amount);
  const [comment, setComment] = useState<
    TalentTaxWithholdingCorrection["comment"]
  >(item.comment ?? "");
  const [date, setDate] = useState<Nullable<Date>>(new Date(item.date));
  const { t } = useTranslation();

  const [errors, setErrors] = useState<Errors>({
    amount: false,
    date: false,
    comment: false,
  });

  const updateDate = (date: Date | null) => {
    const newDate = date as Date;

    setDate(newDate);
  };

  const validateAmount = (amount: Nullable<string>) => {
    if (isValidAmount(amount)) {
      setErrors((prev) => ({ ...prev, amount: false }));
    } else {
      setErrors((prev) => ({ ...prev, amount: true }));
    }
  };

  const validateDate = (date: Nullable<Date>) => {
    if (isValid(date)) {
      setErrors((prev) => ({ ...prev, date: false }));
    } else {
      setErrors((prev) => ({ ...prev, date: true }));
    }
  };

  const validateComment = (comment: Nullable<string>) => {
    if (isValidComment(comment)) {
      setErrors((prev) => ({ ...prev, comment: false }));
    } else {
      setErrors((prev) => ({ ...prev, comment: true }));
    }
  };

  const handleEdit = () => {
    const newDate = format(date as Date, isoFormat);

    const updateItem: UpdateTaxWithholdingCorrection = {
      ...item,
      amount: parseFloat(amount!!.replace(",", ".")).toString(),
      comment: comment ? comment : undefined,
      date: newDate,
    };

    onEditSubmit(updateItem);
    onEditCancel();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRadius: "8px",
        marginTop: "16px",
        padding: "0px 16px 0px 0px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>
          <Typography
            variant="header"
            sx={{ fontWeight: 600, fontSize: "14px" }}
          >
            {t("talentProfile:addWitholdingTaxCorrection").toUpperCase()}
          </Typography>
        </DialogTitle>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px 0px 16px 16px",
          fontSize: "12px",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:correction")}
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:comment")}
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  {t("talentProfile:date")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <TextField
                    sx={{ width: "120px", background: "#F9F9FA" }}
                    name="correction"
                    value={amount}
                    size="small"
                    error={errors.amount}
                    onChange={(e) => {
                      validateAmount(e.target.value);
                      setAmount(e.target.value);
                    }}
                  />
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <TextField
                    sx={{ width: "200px", background: "#F9F9FA" }}
                    name="comment"
                    value={comment}
                    error={errors.comment}
                    size="small"
                    onChange={(e) => {
                      validateComment(e.target.value);
                      setComment(e.target.value);
                    }}
                  />
                </TableCell>
                <TableCell sx={{ paddingTop: "0px", border: "none" }}>
                  <DatePicker
                    format={leadingZerosDateFormat}
                    value={date}
                    onChange={(e) => {
                      validateDate(e);
                      updateDate(e);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        label: null,
                        error: errors.date,
                        sx: {
                          width: "200px",
                          paddingTop: "0px",
                          border: "none",
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginTop="16px"
        >
          <Button
            sx={{ borderRadius: "15px", maxHeight: "30px" }}
            startIcon={<CancelIcon />}
            variant="outlined"
            onClick={onEditCancel}
          >
            {t("common:cancel")}
          </Button>
          <Button
            sx={{ borderRadius: "15px", maxHeight: "30px" }}
            startIcon={<CheckIcon />}
            variant="contained"
            disabled={errors.amount || errors.date || errors.comment}
            onClick={() => {
              handleEdit();
            }}
          >
            {t("common:complete")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddEditModal;
